import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useMemo } from "react";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Clock, Tag } from "lucide-react";
import { getSoldVehicle } from "@/lib/sold-vehicle.functions";
import { getSoldArchive } from "@/lib/sold-archive.functions";
import { buildSoldDetail, brandFromTitle, yearFromTitle } from "@/lib/sold-detail";
import { buildTitle, clampDescription } from "@/lib/seo-quality";


const BASE = "https://www.akhusbilfritid.se";

const soldVehicleQuery = (slug: string) =>
  queryOptions({
    queryKey: ["sold-vehicle", slug],
    queryFn: () => getSoldVehicle({ data: { slug } }),
    staleTime: 30 * 60 * 1000,
  });

const archiveQuery = queryOptions({
  queryKey: ["sold-archive"],
  queryFn: () => getSoldArchive(),
  staleTime: 30 * 60 * 1000,
});

export const Route = createFileRoute("/salda_/$slug")({
  loader: async ({ params, context }) => {
    const [res] = await Promise.all([
      context.queryClient.ensureQueryData(soldVehicleQuery(params.slug)),
      context.queryClient.ensureQueryData(archiveQuery),
    ]);
    if (!res.item) throw notFound();
    return { item: res.item };
  },
  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    const url = `${BASE}/salda/${params.slug}`;
    if (!item) {
      return { meta: [{ title: "Sålt fordon hittades inte — AK Husbil & Fritid" }, { name: "robots", content: "noindex" }] };
    }
    const priceStr = item.price ? `${item.price.toLocaleString("sv-SE")} kr` : "okänt slutpris";
    // Annonsrubriker innehåller ofta säljfraser ("Leverans i hela Sverige", "SÅLD!")
    // som bara äter upp SERP-utrymme — bort med dem innan titeln byggs.
    const cleanName = item.title
      .replace(/[\s—-]*\b(inkommande|s[åa]ld!?|leverans(en)? i hela sverige|lev\.? i hela sverige|nyinkommen|reserverad)\b[!\s]*/gi, " ")
      .replace(/\s{2,}/g, " ")
      .replace(/[\s,;:—–-]+$/, "")
      .trim();
    const title = buildTitle(`${cleanName} — såld för ${priceStr}`, "Slutpriser AK Husbil");
    const desc = clampDescription(
      `${cleanName} såldes ${item.soldAt} för ${priceStr}` +
        (item.daysOnMarket != null ? ` efter ${item.daysOnMarket} dagar` : "") +
        `. Verkligt slutpris, liggetid och prisjämförelse mot vårt sålda lager i Kalix.`,
    );
    const indexable = item.price != null && item.price > 0;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        {

          name: "robots",
          content: indexable ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, follow",
        },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc.slice(0, 158) },
        { property: "og:url", content: url },
        ...(item.image ? [{ property: "og:image", content: item.image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...(item.image ? [{ name: "twitter:image", content: item.image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: item.title,
            image: item.image ?? undefined,
            category: item.category,
            offers: {
              "@type": "Offer",
              price: item.price ?? undefined,
              priceCurrency: "SEK",
              availability: "https://schema.org/SoldOut",
              itemCondition: "https://schema.org/UsedCondition",
              url,
              validThrough: item.soldAt,
              seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid", url: BASE },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: BASE + "/" },
              { "@type": "ListItem", position: 2, name: "Sålda fordon", item: BASE + "/salda" },
              { "@type": "ListItem", position: 3, name: item.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: SoldVehiclePage,
  notFoundComponent: () => (
    <div className="container-edge py-20 text-center">
      <h1 className="font-display text-4xl">Fordonet finns inte i arkivet</h1>
      <p className="mt-3 text-muted-foreground">Se hela arkivet över sålda fordon och slutpriser istället.</p>
      <Link to="/salda" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-primary-foreground">
        <ArrowLeft size={16} /> Till sålda fordon
      </Link>
    </div>
  ),
});

const sek = (n: number | null) => (n == null ? "–" : new Intl.NumberFormat("sv-SE").format(n) + " kr");

function SoldVehiclePage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(soldVehicleQuery(slug));
  const { data: archive } = useSuspenseQuery(archiveQuery);
  const item = data.item!;

  const detail = useMemo(() => {
    const brand = brandFromTitle(item.title);
    const catSeg = archive.byCategory.find((c) => c.label === item.category) ?? null;
    const brandSeg = brand ? archive.byBrand.find((b) => b.label.toLowerCase() === brand.toLowerCase()) ?? null : null;
    return buildSoldDetail(
      {
        title: item.title,
        price: item.price,
        category: item.category,
        soldAt: item.soldAt,
        daysOnMarket: item.daysOnMarket,
        firstSeenAt: item.firstSeenAt,
        image: item.image,
        brand,
        year: yearFromTitle(item.title),
      },
      catSeg,
      brandSeg,
      archive.total,
    );
  }, [item, archive]);

  const similar = archive.vehicles
    .filter((v) => v.title !== item.title && v.category === item.category)
    .slice(0, 4);

  return (
    <div className="bg-warm">
      <div className="container-edge py-6">
        <Link to="/salda" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Sålda fordon & slutpriser
        </Link>
      </div>

      <header className="container-edge grid gap-8 pb-10 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <p className="eyebrow">Slutprisarkiv · sålt fordon</p>
          <h1 className="mt-2 font-display text-3xl leading-tight md:text-4xl">{detail.headline}</h1>
          <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">{detail.lead}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Tag size={14} /> Slutpris
              </p>
              <p className="mt-2 font-display text-2xl text-accent">{sek(item.price)}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Clock size={14} /> Dagar till försäljning
              </p>
              <p className="mt-2 font-display text-2xl">
                {item.daysOnMarket != null ? `${item.daysOnMarket}` : "–"}
              </p>
            </div>
          </div>
        </div>
        {item.image && (
          <img
            src={item.image}
            alt={`${item.title} — såld hos AK Husbil & Fritid`}
            width={1200}
            height={900}
            loading="lazy"
            decoding="async"
            className="aspect-[4/3] w-full rounded-3xl border border-border object-cover"
          />

        )}
      </header>

      <section className="container-edge pb-12">
        <div className="overflow-x-auto rounded-3xl border border-border bg-card">
          <table className="w-full min-w-[420px] border-collapse text-sm">
            <tbody>
              {detail.facts.map((f) => (
                <tr key={f.label} className="border-b border-border/60 last:border-0">
                  <th className="w-1/2 px-5 py-3 text-left font-medium">{f.label}</th>
                  <td className="px-5 py-3 capitalize text-muted-foreground">{f.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container-edge pb-12">
        <h2 className="font-display text-2xl md:text-3xl">Vad priset säger</h2>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          {detail.priceContext.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      <section className="container-edge pb-12">
        <h2 className="font-display text-2xl md:text-3xl">Efterfrågan och liggetid</h2>
        <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">{detail.demand}</p>
      </section>

      <section className="container-edge pb-12">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
          <h2 className="font-display text-2xl md:text-3xl">{detail.checklist.title}</h2>
          <ul className="mt-5 grid gap-2.5 text-sm md:grid-cols-2">
            {detail.checklist.items.map((it, i) => (
              <li key={i} className="inline-flex items-start gap-2">
                <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="container-edge grid gap-6 pb-12 md:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-xl">Ska du köpa något liknande?</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            {detail.buyerAdvice.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-xl">Ska du sälja ett eget?</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            {detail.sellerAdvice.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="container-edge pb-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-card p-6 md:p-10">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-1 font-display text-2xl md:text-3xl">Om det här sålda fordonet</h2>
          <div className="mt-6 divide-y divide-border">
            {detail.faq.map((f, i) => (
              <details key={i} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-medium">
                  <span>{f.q}</span>
                  <span className="text-accent transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {similar.length > 0 && (
        <section className="container-edge pb-12">
          <h2 className="font-display text-2xl md:text-3xl">Andra sålda {item.category === "husvagn" ? "husvagnar" : "husbilar"}</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {similar.map((v, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5">
                <p className="font-medium">{v.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {sek(v.price)} · såld {v.soldAt}
                  {v.daysOnMarket != null ? ` · ${v.daysOnMarket} dagar` : ""}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="container-edge pb-20">
        <div className="grid gap-4 md:grid-cols-3">
          <Next to="/husbilar-till-salu" label="Husbilar i lager nu" desc="Jämför dagens priser mot slutpriset ovan." />
          <Next to="/husvagnar-till-salu" label="Husvagnar i lager nu" desc="Aktuellt, fukttestat och gasoltestat." />
          <Next to="/salda" label="Hela slutprisarkivet" desc="Median, snitt och liggetid per märke." />
        </div>
      </section>
    </div>
  );
}

function Next({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link
      to={to}
      className="group flex items-start justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
    >
      <span>
        <span className="block font-medium group-hover:text-accent">{label}</span>
        <span className="mt-1 block text-sm text-muted-foreground">{desc}</span>
      </span>
      <ArrowUpRight size={16} className="mt-1 shrink-0 text-accent" />
    </Link>
  );
}
